import React, { useMemo } from "react";
import { Typography } from "@mui/material";
import ConnectionAccordionSection from "../../../../components/view/sections/ConnectionAccordionSection";
import EntityReferenceComponent from "../../../../components/entitiesReferences/EntityReferenceComponent";
import { ViewAccordionField } from "../../../../components/view/fields/ViewAccordion";
import { RichTextEditor } from "../../../../components/field/richTextEditor/RichTextEditor";
import usePlaceOfPersonQuery from "../../../../hooks/query/connections/usePlaceOfPersonQuery";
import { Person } from "../../../../types/person";
import {
  PlaceOfPerson,
  SYSTEM_PLACE_OF_PERSON_TYPE_LABELS,
  SystemPlaceOfPersonTypes,
} from "../../../../types/placeOfPerson";

interface PlaceOfPersonViewSectionProps {
  person: Person;
}

const formatPlaceOfPersonType = (
  type: SystemPlaceOfPersonTypes | null
): string =>
  (type && SYSTEM_PLACE_OF_PERSON_TYPE_LABELS[type]) || "N/A";

const getPlaceOfPersonFields = (
  edge: PlaceOfPerson
): ViewAccordionField[] => [
  {
    label: "Place Of Person Type",
    value: (
      <Typography variant="body1">
        {formatPlaceOfPersonType(edge?.system_place_of_person_type ?? null)}
      </Typography>
    ),
  },
  {
    label: "Notes",
    value: (
      <RichTextEditor
        value={typeof edge?.notes === "string" ? edge.notes : ""}
        readOnly
        fullWidth
        placeholder="N/A"
      />
    ),
  },
];

const PlaceOfPersonViewSection: React.FC<PlaceOfPersonViewSectionProps> = ({
  person,
}) => {
  const { useFetchConnectedEdges } = usePlaceOfPersonQuery();
  const { data, isLoading, errorMessage } = useFetchConnectedEdges(
    person?._id,
    "outbound",
    {}
  );

  const items = useMemo<PlaceOfPerson[]>(() => data?.data ?? [], [data]);

  return (
    <ConnectionAccordionSection<PlaceOfPerson>
      title="Places Of Person"
      loading={isLoading}
      error={errorMessage}
      items={items}
      getItemId={(edge, index) => edge?._key ?? `place-of-person-${index}`}
      getItemTitle={(edge) =>
        edge?.toDoc ? (
          <EntityReferenceComponent entity={edge.toDoc} linkMode="view" />
        ) : (
          <Typography variant="body1">N/A</Typography>
        )
      }
      getItemSubtitle={(edge) =>
        formatPlaceOfPersonType(edge?.system_place_of_person_type ?? null)
      }
      getItemFields={(edge) => getPlaceOfPersonFields(edge)}
      emptyText="No places of person"
    />
  );
};

export default PlaceOfPersonViewSection;
